import type { ReportConfig } from '@/types/custom-reports';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { useState } from 'react';

interface Step1DataSourcesProps {
  config: ReportConfig;
  updateConfig: (updates: Partial<ReportConfig>) => void;
}

export function Step1DataSources({ config, updateConfig }: Step1DataSourcesProps) {
  const [showCustomDates, setShowCustomDates] = useState(
    config.dateRange.type === 'custom'
  );

  const dataSourcesOptions = [
    { id: 'transactions', label: 'Transações', description: 'Todas as transações financeiras' },
    { id: 'categories', label: 'Categorias', description: 'Agrupamento por categoria' },
    { id: 'cost_centers', label: 'Centros de Custo', description: 'Departamentos e áreas' },
    { id: 'clients', label: 'Clientes', description: 'Para receitas' },
    { id: 'suppliers', label: 'Fornecedores', description: 'Para despesas' },
  ];

  const toggleDataSource = (source: string) => {
    const newDataSources = config.dataSources.includes(source as any)
      ? config.dataSources.filter(s => s !== source)
      : [...config.dataSources, source as any];
    updateConfig({ dataSources: newDataSources });
  };

  const updateDateRangeType = (type: ReportConfig['dateRange']['type']) => {
    if (type === 'custom') {
      setShowCustomDates(true);
      updateConfig({
        dateRange: { type, start: new Date().toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] }
      });
    } else {
      setShowCustomDates(false);
      updateConfig({ dateRange: { type } });
    }
  };

  return (
    <div className="space-y-6">
      {/* Data Sources */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Fontes de Dados</Label>
        <p className="text-sm text-muted-foreground">Selecione quais dados deseja incluir no relatório</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {dataSourcesOptions.map((source) => (
            <div key={source.id} className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
              <Checkbox
                id={source.id}
                checked={config.dataSources.includes(source.id as any)}
                onCheckedChange={() => toggleDataSource(source.id)}
              />
              <div className="flex-1 space-y-1">
                <Label htmlFor={source.id} className="cursor-pointer font-medium">
                  {source.label}
                </Label>
                <p className="text-xs text-muted-foreground">{source.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Date Range */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Período de Análise</Label>
        <Select
          value={config.dateRange.type}
          onValueChange={(value) => updateDateRangeType(value as ReportConfig['dateRange']['type'])}
        >
          <SelectTrigger>
            <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Hoje</SelectItem>
            <SelectItem value="this_week">Esta Semana</SelectItem>
            <SelectItem value="this_month">Este Mês</SelectItem>
            <SelectItem value="last_month">Mês Passado</SelectItem>
            <SelectItem value="custom">Personalizado</SelectItem>
          </SelectContent>
        </Select>

        {showCustomDates && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="start-date">Data Início</Label>
              <input
                id="start-date"
                type="date"
                value={config.dateRange.start || ''}
                onChange={(e) => updateConfig({ dateRange: { ...config.dateRange, start: e.target.value } })}
                className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">Data Fim</Label>
              <input
                id="end-date"
                type="date"
                value={config.dateRange.end || ''}
                onChange={(e) => updateConfig({ dateRange: { ...config.dateRange, end: e.target.value } })}
                className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
              />
            </div>
          </div>
        )}
      </div>

      {/* Transaction Type */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Tipo de Transação</Label>
        <Select
          value={config.filters.transactionType}
          onValueChange={(value) => updateConfig({ filters: { ...config.filters, transactionType: value as any } })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="income">Apenas Receitas</SelectItem>
            <SelectItem value="expense">Apenas Despesas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Status Filter */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Status</Label>
        <Select
          value={config.filters.status || 'all'}
          onValueChange={(value) => updateConfig({ filters: { ...config.filters, status: value as any } })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="paid">Pagas</SelectItem>
            <SelectItem value="pending">Pendentes</SelectItem>
            <SelectItem value="overdue">Atrasadas</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
